/**
 * Analytics Event Tracking
 * Centralized event tracking functions for all analytics platforms
 */

import { EVENT_NAMES, ECOMMERCE_DEFAULTS, X_PIXEL, META_PIXEL } from "./config";
import type {
  EventParameters,
  LeadData,
  NewsletterSignupData,
  FormInteractionData,
  XPixelEventData,
  MetaPixelEventData,
  ProductViewData,
  PurchaseData,
} from "./types";
import { hasAnalyticsConsent, isDataLayerAvailable } from "./types";
import { buildGoogleUserData } from "./user-data";

// ============================================================================
// Core Event Tracking
// ============================================================================

/**
 * Generic event tracking function
 * Tracks events to Google Analytics via dataLayer (GTM standard)
 * and to PostHog via window.posthog.capture().
 *
 * @param eventName - Name of the event to track
 * @param parameters - Event parameters
 */
export function trackEvent(eventName: string, parameters: EventParameters = {}) {
  if (!hasAnalyticsConsent()) {
    console.log(`Analytics consent not given, skipping event: ${eventName}`);
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn(`dataLayer not available, cannot track event: ${eventName}`);
    return;
  }

  window.dataLayer?.push({ event: eventName, ...parameters });
  window.posthog?.capture?.(eventName, parameters);
  console.log(`Event tracked: ${eventName}`, parameters);
}

// ============================================================================
// Ecommerce Event Tracking
// ============================================================================

/**
 * Parse a localized money string into a number of major currency units.
 *
 * LemonSqueezy substitutes a LOCALIZED total into the success-URL param
 * (e.g. "$7.41", "178,49 zł", "R$ 6,99", "59,49 €"). The old
 * `.replace(/[^0-9.]/g, "")` sanitizer stripped the comma from
 * comma-decimal locales, silently multiplying those totals by 100
 * ("6,99" -> 699). Verified against real purchase events in PostHog
 * (2026-07-22): every x100-looking value divided cleanly into a
 * comma-decimal price, and one Polish buyer's order appeared as both
 * 17849.0 (comma link) and 178.49 (dot link).
 *
 * Rule: when both separators appear, the LAST one is the decimal mark
 * ("1.234,56" -> 1234.56, "1,234.56" -> 1234.56). A lone comma is a
 * decimal mark only when followed by 1-2 trailing digits ("6,99"),
 * otherwise a thousands separator ("12,345").
 *
 * Note: the returned amount is in the ORDER currency (LS localizes the
 * total), so non-USD orders report their native-unit amount. Fixing that
 * would need server-side/webhook tracking — out of reach for this static
 * site. What IS fixable client-side is the currency LABEL: the localized
 * total carries its own currency marker, which detectLocalizedCurrency
 * resolves so revenue is no longer mislabelled as USD.
 */
export function parseLocalizedAmount(raw: string): number {
  const cleaned = raw.replace(/[^0-9.,]/g, "");
  const lastComma = cleaned.lastIndexOf(",");
  const lastDot = cleaned.lastIndexOf(".");

  const normalized =
    lastComma > lastDot
      ? // Comma-decimal locale: dots (if any) are thousands separators.
        // Keep the comma only as a decimal mark with 1-2 trailing digits.
        cleaned
          .replace(/\./g, "")
          .replace(/,(\d{1,2})$/, ".$1")
          .replace(/,/g, "")
      : // Dot-decimal locale (or no separator): commas are thousands separators.
        cleaned.replace(/,/g, "");

  return parseFloat(normalized);
}

/**
 * Currencies LemonSqueezy can localise a total into, mapped from their
 * ISO-4217 code. This is the AUTHORITATIVE source: an explicit 3-letter code in
 * the string is unambiguous, so it resolves directly without pattern matching.
 *
 * Kept as its own map rather than folded into the symbol table below, because a
 * single pattern row that lists several codes (e.g. one row matching
 * `NOK|DKK|SEK`) can only ever return one of them — that mistake shipped DKK
 * and NOK orders labelled SEK, and CNY labelled JPY.
 */
const ISO_CURRENCY_CODES: Readonly<Record<string, string>> = {
  PLN: "PLN",
  BRL: "BRL",
  EUR: "EUR",
  SEK: "SEK",
  NOK: "NOK",
  DKK: "DKK",
  ZAR: "ZAR",
  GBP: "GBP",
  INR: "INR",
  JPY: "JPY",
  CNY: "CNY",
  MXN: "MXN",
};

/**
 * Currency symbols for totals that carry no ISO code. Order totals arrive as
 * e.g. "$7.41", "178,49 zł", "R$ 6,99", "59,49 €" — the amount is always in the
 * ORDER currency, so the value must not be labelled "USD".
 *
 * Every entry here is genuinely ambiguous or already covered by the ISO map
 * above, so these rows are only reached when no code is present:
 *   - "kr" is shared by SEK/NOK/DKK and "Kč" is not listed at all.
 *   - "$" alone spans USD/CAD/AUD/MXN, so it is deliberately absent and falls
 *     through to the default rather than guessing.
 *   - "R" alone is ZAR in South Africa but a prefix of "R$" (BRL), which is why
 *     BRL is matched first and "R" uses a word boundary.
 * Ordered most-specific first.
 */
const CURRENCY_SYMBOLS: ReadonlyArray<[RegExp, string]> = [
  [/z\s*ł/i, "PLN"],
  [/\bR\$/i, "BRL"],
  [/€/, "EUR"],
  [/\bkr\b/i, "SEK"],
  [/\bkrn\b/i, "NOK"],
  [/\bR\b/i, "ZAR"],
  [/£/, "GBP"],
  [/₹/, "INR"],
  [/¥/, "JPY"],
];

/**
 * Resolve the order currency from a LemonSqueezy localized total.
 *
 * This fixes the CURRENCY LABEL, not the AMOUNT: a Polish order reporting
 * 178.49 PLN is now correctly denominated, but converting it to USD still
 * requires an FX rate that a static client site has no access to. Consumers
 * that aggregate across currencies must normalise using their own rate table
 * (PostHog does this from the currency code alone).
 *
 * Resolution order:
 *   1. An explicit ISO-4217 code anywhere in the string (authoritative).
 *   2. A currency symbol (ambiguous by nature, so only unambiguous ones are
 *      listed — see CURRENCY_SYMBOLS).
 *   3. The configured fallback.
 *
 * @param raw - The localized money string, e.g. "178,49 zł"
 * @param fallback - Used when the marker is unrecognised or absent
 */
export function detectLocalizedCurrency(
  raw: string,
  fallback: string = ECOMMERCE_DEFAULTS.CURRENCY
): string {
  if (!raw) return fallback;

  // 1. Explicit ISO code wins over any symbol, e.g. "$7.41 PLN" or "¥120 CNY".
  const isoMatch = raw.toUpperCase().match(/\b([A-Z]{3})\b/);
  if (isoMatch) {
    const resolved = ISO_CURRENCY_CODES[isoMatch[1]];
    if (resolved) return resolved;
  }

  // 2. Symbol fallback.
  for (const [pattern, code] of CURRENCY_SYMBOLS) {
    if (pattern.test(raw)) return code;
  }

  // 3. Nothing recognisable — never guess.
  return fallback;
}

/**
 * Track product view event
 * Fires when a user views a product page
 *
 * @param product - Product data
 */
export function trackProductView(product: ProductViewData) {
  if (!hasAnalyticsConsent()) {
    console.log(`Analytics consent not given, skipping: trackProductView`);
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn("dataLayer not available, cannot track: trackProductView");
    return;
  }

  const price = parseFloat(product.price);

  window.dataLayer?.push({
    event: EVENT_NAMES.VIEW_PRODUCT,
    ecommerce: {
      currency: ECOMMERCE_DEFAULTS.CURRENCY,
      value: price,
      items: [
        {
          item_id: product.slug,
          item_name: product.alternateName || product.name,
          item_category: product.category || ECOMMERCE_DEFAULTS.DEFAULT_CATEGORY,
          price,
          quantity: 1,
        },
      ],
    },
  });

  window.posthog?.capture?.(EVENT_NAMES.VIEW_PRODUCT, {
    product_id: product.slug,
    product_name: product.alternateName || product.name,
    product_category: product.category || ECOMMERCE_DEFAULTS.DEFAULT_CATEGORY,
    price,
    currency: ECOMMERCE_DEFAULTS.CURRENCY,
  });

  // Meta Pixel ViewContent — used by Meta to train Lookalike audiences and
  // dynamic-ads catalogs. Soft-fails on no consent / blocked pixel.
  trackMetaPixelViewContent({
    value: price,
    currency: ECOMMERCE_DEFAULTS.CURRENCY,
    content_ids: [product.slug],
    content_name: product.alternateName || product.name,
    content_type: "product",
  });

  console.log(`Event tracked: ${EVENT_NAMES.VIEW_PRODUCT}`, { product: product.name, price });
}

/**
 * Track add to cart event
 *
 * @param product - Product being added to cart
 * @param value - Cart value (defaults to product price)
 */
export function trackAddToCart(
  product: { id: string; name: string; price?: number; variant?: string },
  value?: number
) {
  if (!hasAnalyticsConsent()) {
    console.log(`Analytics consent not given, skipping: trackAddToCart`);
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn("dataLayer not available, cannot track: trackAddToCart");
    return;
  }

  const cartValue = value || product.price || parseFloat(ECOMMERCE_DEFAULTS.DEFAULT_VALUE);

  window.dataLayer?.push({
    event: EVENT_NAMES.ADD_TO_CART,
    ecommerce: {
      currency: ECOMMERCE_DEFAULTS.CURRENCY,
      value: cartValue,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_variant: product.variant,
        },
      ],
    },
  });

  window.posthog?.capture?.(EVENT_NAMES.ADD_TO_CART, {
    product_id: product.id,
    product_name: product.name,
    product_variant: product.variant,
    value: cartValue,
    currency: ECOMMERCE_DEFAULTS.CURRENCY,
  });

  console.log(`Event tracked: ${EVENT_NAMES.ADD_TO_CART}`, {
    product: product.name,
    value: cartValue,
    variant: product.variant,
  });
}

/**
 * Track begin checkout event
 *
 * @param product - Product being checked out
 * @param value - Checkout value
 */
export function trackBeginCheckout(
  product: { id: string; name: string; price?: number; variant?: string },
  value?: number
) {
  if (!hasAnalyticsConsent()) {
    console.log(`Analytics consent not given, skipping: trackBeginCheckout`);
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn("dataLayer not available, cannot track: trackBeginCheckout");
    return;
  }

  const checkoutValue = value || product.price || parseFloat(ECOMMERCE_DEFAULTS.DEFAULT_VALUE);

  window.dataLayer?.push({
    event: EVENT_NAMES.BEGIN_CHECKOUT,
    ecommerce: {
      currency: ECOMMERCE_DEFAULTS.CURRENCY,
      value: checkoutValue,
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          item_variant: product.variant,
        },
      ],
    },
  });

  window.posthog?.capture?.(EVENT_NAMES.BEGIN_CHECKOUT, {
    product_id: product.id,
    product_name: product.name,
    product_variant: product.variant,
    value: checkoutValue,
    currency: ECOMMERCE_DEFAULTS.CURRENCY,
  });

  console.log(`Event tracked: ${EVENT_NAMES.BEGIN_CHECKOUT}`, {
    product: product.name,
    value: checkoutValue,
    variant: product.variant,
  });
}

/**
 * Track purchase event
 * Fires when a purchase is completed
 *
 * @param purchaseData - Purchase transaction data
 */
export function trackPurchase(purchaseData: PurchaseData) {
  if (!hasAnalyticsConsent()) {
    console.log(`Analytics consent not given, skipping: trackPurchase`);
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn("dataLayer not available, cannot track: trackPurchase");
    return;
  }

  const transactionId =
    purchaseData.orderId || purchaseData.orderIdentifier || `purchase-${Date.now()}`;

  const purchaseValue = purchaseData.total
    ? parseLocalizedAmount(purchaseData.total)
    : parseFloat(purchaseData.product.price);

  // The LemonSqueezy total is denominated in the ORDER currency, so label it
  // as such instead of assuming USD. See detectLocalizedCurrency for what this
  // does and does not fix.
  const orderCurrency = purchaseData.total
    ? detectLocalizedCurrency(purchaseData.total)
    : ECOMMERCE_DEFAULTS.CURRENCY;

  // The buyer's email, hashed in the browser, rides along as user_data (see
  // lib/analytics/user-data.ts); the push waits for the hash.
  void buildGoogleUserData(purchaseData.email).then(
    (userData) =>
      hasAnalyticsConsent() &&
      window.dataLayer?.push({
        event: EVENT_NAMES.PURCHASE,
        ...(userData && { user_data: userData }),
        ecommerce: {
          transaction_id: transactionId,
          value: purchaseValue,
          currency: orderCurrency,
          tax: 0,
          shipping: 0,
          items: [
            {
              item_id: purchaseData.product.slug,
              item_name: purchaseData.product.alternateName || purchaseData.product.name,
              item_category: ECOMMERCE_DEFAULTS.DEFAULT_CATEGORY,
              price: purchaseValue,
              quantity: 1,
            },
          ],
        },
      })
  );

  // PostHog uses `revenue` as the conventional property name so this event
  // surfaces in PostHog's revenue analytics out of the box.
  window.posthog?.capture?.(EVENT_NAMES.PURCHASE, {
    transaction_id: transactionId,
    product_id: purchaseData.product.slug,
    product_name: purchaseData.product.alternateName || purchaseData.product.name,
    product_category: ECOMMERCE_DEFAULTS.DEFAULT_CATEGORY,
    revenue: purchaseValue,
    value: purchaseValue,
    currency: orderCurrency,
  });

  console.log(`Event tracked: ${EVENT_NAMES.PURCHASE}`, {
    product: purchaseData.product.name,
    transactionId,
    value: purchaseValue,
  });
}

// ============================================================================
// Lead Generation Event Tracking
// ============================================================================

/**
 * Track a genuine sales lead.
 *
 * Fires only when someone asks us for something — a custom extension quote, a
 * sales enquiry. Newsletter subscribers go to trackNewsletterSignup instead.
 *
 * GA4 documents `value`/`currency` as its value parameters for this event, so
 * budget travels as a number when known; `budget_range` stays alongside for
 * the qualitative read.
 */
export function trackLeadSubmission(leadData: LeadData) {
  if (!hasAnalyticsConsent()) {
    console.log("Analytics consent not given, skipping: trackLeadSubmission");
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn("dataLayer not available, cannot track: trackLeadSubmission");
    return;
  }

  const currency = leadData.currency ?? ECOMMERCE_DEFAULTS.CURRENCY;

  // Only send value when we actually know it — GA4 treats value+currency as
  // required together, and a zero value would read as "a lead worth $0"
  // rather than "budget not captured".
  const valueParams =
    typeof leadData.budget_value === "number" ? { value: leadData.budget_value, currency } : {};

  const params = {
    form_name: leadData.form_name,
    form_id: leadData.form_id,
    form_source: leadData.form_source,
    lead_source: leadData.lead_source,
    budget_range: leadData.budget_range,
    company_provided: leadData.company_provided,
    ...valueParams,
  };

  // Hashed email for Google as user_data (lib/analytics/user-data.ts); never in params/PostHog
  void buildGoogleUserData(leadData.email).then(
    (userData) =>
      hasAnalyticsConsent() &&
      window.dataLayer?.push({
        event: EVENT_NAMES.GENERATE_LEAD,
        ...params,
        ...(userData && { user_data: userData }),
      })
  );

  window.posthog?.capture?.(EVENT_NAMES.GENERATE_LEAD, params);

  // Meta and X Lead conversions. Correct here — these are people who asked to
  // buy something. Newsletter signups deliberately do NOT reach these.
  trackMetaPixelLead({
    content_name: leadData.form_name,
    value: leadData.budget_value ?? 0,
    currency,
  });

  trackXPixelLead({
    conversion_id: `${leadData.form_id || leadData.form_name}-${Date.now()}`,
  });

  console.log(`Event tracked: ${EVENT_NAMES.GENERATE_LEAD}`, { form: leadData.form_name });
}

/**
 * Back-compat alias. Kept because window.trackLead is a documented global
 * consumed by form components that have not all migrated yet; it is no longer
 * used for newsletter signups.
 */
export const trackLead = trackLeadSubmission;

/**
 * Track an email newsletter subscription.
 *
 * This is audience growth, not a sales lead. It deliberately does NOT fire the
 * Meta or X Lead conversions — doing so inflated lead ROAS with people who only
 * wanted product-update emails.
 */
export function trackNewsletterSignup(data: NewsletterSignupData) {
  if (!hasAnalyticsConsent()) {
    console.log("Analytics consent not given, skipping: trackNewsletterSignup");
    return;
  }

  if (!isDataLayerAvailable()) {
    console.warn("dataLayer not available, cannot track: trackNewsletterSignup");
    return;
  }

  const params = {
    form_id: data.form_id,
    form_source: data.form_source,
  };

  // Hashed email for Google as user_data (lib/analytics/user-data.ts); never in params/PostHog
  void buildGoogleUserData(data.email).then(
    (userData) =>
      hasAnalyticsConsent() &&
      window.dataLayer?.push({
        event: EVENT_NAMES.NEWSLETTER_SIGNUP,
        ...params,
        ...(userData && { user_data: userData }),
      })
  );

  window.posthog?.capture?.(EVENT_NAMES.NEWSLETTER_SIGNUP, params);

  console.log(`Event tracked: ${EVENT_NAMES.NEWSLETTER_SIGNUP}`, { source: data.form_source });
}

/**
 * Track form interaction mechanics: form_start on first field focus,
 * form_submit on a confirmed successful submission.
 *
 * These are GA4 enhanced-measurement event names, emitted manually because our
 * forms submit over fetch() and EM's native-submit detection cannot see them.
 * Enhanced measurement form interactions MUST stay disabled — enabling it
 * alongside this double-counts both events.
 */
export function trackFormInteraction(stage: "start" | "submit", data: FormInteractionData) {
  if (!hasAnalyticsConsent()) {
    return;
  }

  if (!isDataLayerAvailable()) {
    return;
  }

  const eventName = stage === "start" ? EVENT_NAMES.FORM_START : EVENT_NAMES.FORM_SUBMIT;

  const params = {
    form_id: data.form_id,
    // Not every surface names itself twice. form_id is the stable identity, so
    // fall back to it rather than pushing an empty form_name into GA4 — an
    // empty custom param still occupies the column and reports as "(not set)".
    form_name: data.form_name ?? data.form_id,
    form_source: data.form_source,
  };

  window.dataLayer?.push({
    event: eventName,
    ...params,
  });

  window.posthog?.capture?.(eventName, params);
}

// ============================================================================
// X Pixel (Twitter) Event Tracking
// ============================================================================

/**
 * Track X Pixel event with retry logic
 * Handles adblocker detection and graceful degradation
 *
 * @param eventId - X Pixel event ID
 * @param data - Event data
 * @param maxRetries - Maximum number of retry attempts (default: 5)
 */
function trackXPixelEvent(eventId: string, data: Record<string, unknown> = {}, maxRetries = 5) {
  if (typeof window === "undefined") {
    console.log("Window not defined - SSR context");
    return;
  }

  if (window.__xPixelBlocked) {
    console.log("X pixel is blocked - skipping event tracking");
    return;
  }

  if (!hasAnalyticsConsent()) {
    console.log("Analytics blocked by user - not tracking X pixel event");
    return;
  }

  let retryCount = 0;

  const attemptTracking = () => {
    if (!hasAnalyticsConsent()) return;
    try {
      if (typeof window.twq === "function" && window.twq.queue) {
        // Pass the caller-provided data through verbatim. The `contents`
        // array, if any, is the caller's responsibility — Lead and similar
        // form events have no product context and shouldn't synthesize one.
        window.twq("event", eventId, data);
        console.log("X pixel event tracked:", { eventId, ...data });
      } else if (window.__xPixelBlocked) {
        console.log("X pixel blocked - stopping retry attempts");
        return;
      } else {
        retryCount++;
        console.log(`Waiting for twq... (attempt ${retryCount}/${maxRetries})`);

        if (retryCount < maxRetries) {
          setTimeout(attemptTracking, 1000);
        } else {
          console.warn("Failed to track X pixel event: Max retries reached");
          window.__xPixelBlocked = true;
        }
      }
    } catch (error) {
      console.warn("Error tracking X pixel event (pixel may be blocked):", error);
      window.__xPixelBlocked = true;
    }
  };

  attemptTracking();
}

/**
 * Track X Pixel lead event
 *
 * @param data - Lead event data
 */
export function trackXPixelLead(data?: XPixelEventData) {
  trackXPixelEvent(X_PIXEL.EVENTS.LEAD, {
    value: data?.value || "0",
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    conversion_id: data?.conversion_id || `lead-${Date.now()}`,
  });
}

/**
 * Track X Pixel download event
 *
 * @param data - Download event data
 */
export function trackXPixelDownload(data?: XPixelEventData) {
  trackXPixelEvent(X_PIXEL.EVENTS.DOWNLOAD, {
    value: data?.value || "0",
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    conversion_id: data?.conversion_id || `download-${Date.now()}`,
  });
}

/**
 * Build the X Pixel `contents` array for an event tied to a specific product.
 * Returns undefined when no product context is supplied so the event is sent
 * without a synthetic placeholder (Lead, generic Download, etc.).
 */
function buildXPixelContents(product?: { slug: string; name: string }):
  | {
      contents: Array<{
        content_type: string;
        content_id: string;
        content_name: string;
        num_items: number;
      }>;
    }
  | undefined {
  if (!product) return undefined;
  return {
    contents: [
      {
        content_type: "Software > Computer Software",
        content_id: product.slug,
        content_name: product.name,
        num_items: 1,
      },
    ],
  };
}

/**
 * Track X Pixel add to cart event. Pass `product` to attach catalog info.
 *
 * @param data - Add to cart event data (optionally including product)
 */
export function trackXPixelAddToCart(
  data?: XPixelEventData & { product?: { slug: string; name: string } }
) {
  trackXPixelEvent(X_PIXEL.EVENTS.ADD_TO_CART, {
    value: data?.value || ECOMMERCE_DEFAULTS.DEFAULT_VALUE,
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    conversion_id: data?.conversion_id || `cart-${Date.now()}`,
    ...buildXPixelContents(data?.product),
  });
}

/**
 * Track X Pixel purchase event. Pass `product` to attach catalog info.
 *
 * @param data - Purchase event data (optionally including product)
 */
export function trackXPixelPurchase(
  data?: XPixelEventData & { product?: { slug: string; name: string } }
) {
  trackXPixelEvent(X_PIXEL.EVENTS.PURCHASE, {
    value: data?.value || ECOMMERCE_DEFAULTS.DEFAULT_VALUE,
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    conversion_id: data?.conversion_id || `purchase-${Date.now()}`,
    ...buildXPixelContents(data?.product),
  });
}

// ============================================================================
// Meta Pixel (Facebook) Event Tracking
// ============================================================================

/**
 * Internal: fire a single Meta Pixel event with consent and block guards.
 *
 * Soft-fails when:
 *   - SSR (no window)
 *   - User has not consented to analytics
 *   - User has explicitly blocked analytics via consent banner
 *   - Meta Pixel was blocked by the user's adblocker (~30% expected rate)
 *
 * Unlike X Pixel tracking, this does not retry — Meta's `fbq` queues calls
 * itself before `fbevents.js` finishes loading, so a single attempt suffices.
 */
function trackMetaPixelEvent(eventName: string, data: MetaPixelEventData = {}) {
  if (typeof window === "undefined") return;
  if (!hasAnalyticsConsent()) {
    console.log(`Analytics consent not given, skipping Meta event: ${eventName}`);
    return;
  }
  if (window.__metaPixelBlocked) {
    console.log("Meta pixel is blocked - skipping event tracking");
    return;
  }
  if (typeof window.fbq !== "function") {
    console.log("Meta pixel not yet available - skipping event:", eventName);
    return;
  }

  try {
    window.fbq("track", eventName, data);
    console.log("Meta pixel event tracked:", { event: eventName, ...data });
  } catch (error) {
    console.warn("Error tracking Meta pixel event (pixel may be blocked):", error);
    window.__metaPixelBlocked = true;
  }
}

/**
 * Track Meta Pixel Lead event — email capture, contact form submit, etc.
 */
export function trackMetaPixelLead(data?: MetaPixelEventData) {
  trackMetaPixelEvent(META_PIXEL.EVENTS.LEAD, {
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    value: data?.value ?? 0,
    ...data,
  });
}

/**
 * Track Meta Pixel ViewContent event — landing on a product/pricing/learn page.
 */
export function trackMetaPixelViewContent(data?: MetaPixelEventData) {
  trackMetaPixelEvent(META_PIXEL.EVENTS.VIEW_CONTENT, {
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    content_type: data?.content_type || "product",
    ...data,
  });
}

/**
 * Track Meta Pixel InitiateCheckout event — buy button clicked, checkout opened.
 */
export function trackMetaPixelInitiateCheckout(data?: MetaPixelEventData) {
  trackMetaPixelEvent(META_PIXEL.EVENTS.INITIATE_CHECKOUT, {
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    value: data?.value ?? parseFloat(ECOMMERCE_DEFAULTS.DEFAULT_VALUE),
    content_type: data?.content_type || "product",
    num_items: data?.num_items ?? 1,
    ...data,
  });
}

/**
 * Track Meta Pixel Purchase event — one-time purchase completed.
 */
export function trackMetaPixelPurchase(data?: MetaPixelEventData) {
  trackMetaPixelEvent(META_PIXEL.EVENTS.PURCHASE, {
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    value: data?.value ?? parseFloat(ECOMMERCE_DEFAULTS.DEFAULT_VALUE),
    content_type: data?.content_type || "product",
    num_items: data?.num_items ?? 1,
    ...data,
  });
}

/**
 * Track Meta Pixel Subscribe event — recurring subscription started.
 * Distinct from Purchase so Meta can train on subscription LTV separately.
 */
export function trackMetaPixelSubscribe(data?: MetaPixelEventData) {
  trackMetaPixelEvent(META_PIXEL.EVENTS.SUBSCRIBE, {
    currency: data?.currency || ECOMMERCE_DEFAULTS.CURRENCY,
    value: data?.value ?? parseFloat(ECOMMERCE_DEFAULTS.DEFAULT_VALUE),
    content_type: data?.content_type || "product",
    ...data,
  });
}

// ============================================================================
// Combined Tracking Functions
// ============================================================================

/**
 * Track complete purchase across all platforms
 * Fires purchase event to GA4 via dataLayer and to X Pixel / Meta Pixel
 *
 * @param purchaseData - Purchase data
 */
export function trackCompletePurchase(purchaseData: PurchaseData) {
  // Track GA4 via dataLayer
  trackPurchase(purchaseData);

  const purchaseValue = purchaseData.total
    ? parseLocalizedAmount(purchaseData.total)
    : parseFloat(purchaseData.product.price);

  // Same order-currency resolution as trackPurchase — the localized total is
  // denominated in the order currency, so ad destinations must be told so
  // rather than assuming USD.
  const orderCurrency = purchaseData.total
    ? detectLocalizedCurrency(purchaseData.total)
    : ECOMMERCE_DEFAULTS.CURRENCY;

  // Track X Pixel purchase
  const transactionId =
    purchaseData.orderId || purchaseData.orderIdentifier || `purchase-${Date.now()}`;

  trackXPixelPurchase({
    value: purchaseValue.toString(),
    currency: orderCurrency,
    conversion_id: transactionId,
    product: {
      slug: purchaseData.product.slug,
      name: purchaseData.product.alternateName || purchaseData.product.name,
    },
  });

  // Meta Pixel: LemonSqueezy is configured with the same Meta Pixel ID
  // (36007767398837081) and fires Purchase from their side — where they
  // have payment-success ground truth and never double-fire on failed
  // transactions. We intentionally DO NOT fire Purchase here to avoid
  // double-counting revenue in Meta Events Manager.
  //
  // We DO fire Subscribe for monthly/yearly purchases because LS only
  // fires Purchase and doesn't distinguish recurring vs one-time.
  // Subscribe is additive (different event type), not a duplicate, and
  // gives Meta a separate signal for subscription LTV optimization.
  //
  // Detection: any total below the lifetime price tier is a subscription
  // (monthly or yearly). Lifetime purchases pay the full price and fall
  // through to "Purchase only via LS".
  const lifetimePrice = parseFloat(purchaseData.product.price);
  const isSubscription = !Number.isNaN(lifetimePrice) && purchaseValue < lifetimePrice;

  if (isSubscription) {
    trackMetaPixelSubscribe({
      value: purchaseValue,
      currency: orderCurrency,
      content_ids: [purchaseData.product.slug],
      content_name: purchaseData.product.alternateName || purchaseData.product.name,
      content_type: "product",
      event_id: transactionId,
    });
  }
}

/**
 * Track add to cart + begin checkout (for direct checkout flows)
 * Fires both events since user goes straight to checkout
 *
 * @param product - Product data
 * @param source - Traffic source (optional)
 */
export function trackDirectCheckout(
  product: { id: string; name: string; price?: number; variant?: string },
  source?: string
) {
  // Track add to cart
  trackAddToCart(product);

  // Track begin checkout
  trackBeginCheckout(product);

  // Track X Pixel add to cart
  trackXPixelAddToCart({
    conversion_id: `cart-${product.id}-${source || "unknown"}-${Date.now()}`,
    product: { slug: product.id, name: product.name },
  });

  // Track Meta Pixel InitiateCheckout — fires when the buy button is clicked
  // and the LemonSqueezy overlay is about to open.
  const checkoutValue = product.price || parseFloat(ECOMMERCE_DEFAULTS.DEFAULT_VALUE);
  trackMetaPixelInitiateCheckout({
    value: checkoutValue,
    currency: ECOMMERCE_DEFAULTS.CURRENCY,
    content_ids: [product.id],
    content_name: product.name,
    content_type: "product",
    num_items: 1,
  });
}

// ============================================================================
// Thank-You Activation Flow Tracking
// ============================================================================

interface ThankYouEventParameters {
  product_slug?: string;
  order_identifier?: string;
}

/**
 * Track that a user copied their license key on the thank-you page.
 */
export function trackThankYouKeyCopied(params: ThankYouEventParameters = {}) {
  trackEvent(EVENT_NAMES.THANK_YOU_KEY_COPIED, {
    event_category: "activation",
    event_label: "license_key_copy",
    product_slug: params.product_slug ?? "unknown",
    order_identifier: params.order_identifier ?? "unknown",
  });
}

/**
 * Track that a user downloaded their license key as a .txt file.
 */
export function trackThankYouKeyDownloaded(params: ThankYouEventParameters = {}) {
  trackEvent(EVENT_NAMES.THANK_YOU_KEY_DOWNLOADED, {
    event_category: "activation",
    event_label: "license_key_download",
    product_slug: params.product_slug ?? "unknown",
    order_identifier: params.order_identifier ?? "unknown",
  });
}

/**
 * Track that a user clicked Print on the thank-you page.
 */
export function trackThankYouPrintClicked(params: ThankYouEventParameters = {}) {
  trackEvent(EVENT_NAMES.THANK_YOU_PRINT_CLICKED, {
    event_category: "activation",
    event_label: "print_receipt",
    product_slug: params.product_slug ?? "unknown",
    order_identifier: params.order_identifier ?? "unknown",
  });
}

// ============================================================================
// Uninstall Save-the-Customer Offer Events
// ============================================================================

interface UninstallOfferEventParameters {
  product_slug?: string;
  coupon_code?: string;
}

/**
 * Track that the retention offer was shown to a user who selected
 * "It's too expensive" as their uninstall reason.
 */
export function trackUninstallOfferShown(params: UninstallOfferEventParameters = {}) {
  trackEvent(EVENT_NAMES.UNINSTALL_OFFER_SHOWN, {
    event_category: "retention",
    event_label: "offer_shown",
    product_slug: params.product_slug ?? "unknown",
    coupon_code: params.coupon_code ?? "unknown",
  });
}

/**
 * Track that the user copied the retention offer's coupon code to clipboard.
 */
export function trackUninstallOfferCouponCopied(params: UninstallOfferEventParameters = {}) {
  trackEvent(EVENT_NAMES.UNINSTALL_OFFER_COUPON_COPIED, {
    event_category: "retention",
    event_label: "coupon_copied",
    product_slug: params.product_slug ?? "unknown",
    coupon_code: params.coupon_code ?? "unknown",
  });
}

/**
 * Track that the user clicked the "Apply discount & continue" CTA in the
 * retention offer (i.e., went to pricing instead of uninstalling).
 */
export function trackUninstallOfferCtaClicked(params: UninstallOfferEventParameters = {}) {
  trackEvent(EVENT_NAMES.UNINSTALL_OFFER_CTA_CLICKED, {
    event_category: "retention",
    event_label: "cta_clicked",
    product_slug: params.product_slug ?? "unknown",
    coupon_code: params.coupon_code ?? "unknown",
  });
}

/**
 * Track that the user submitted uninstall feedback despite seeing the
 * retention offer (proxy for "saw it, didn't take it").
 */
export function trackUninstallOfferDismissed(params: UninstallOfferEventParameters = {}) {
  trackEvent(EVENT_NAMES.UNINSTALL_OFFER_DISMISSED, {
    event_category: "retention",
    event_label: "offer_dismissed",
    product_slug: params.product_slug ?? "unknown",
    coupon_code: params.coupon_code ?? "unknown",
  });
}

// ============================================================================
// Install Funnel Tracking
// ============================================================================

interface InstallFunnelEventParameters {
  product_slug?: string;
  source?: string;
  source_id?: string;
}

/**
 * Track a click on any "Install Free" CTA (InstallButton).
 *
 * Fills the funnel blind spot between view_product and the /install/
 * pageview: the X pixel fires a Download here, but PostHog previously saw
 * nothing, so on-site install intent could not be segmented by source
 * (product_hero, pricing_free_card, bottom_cta, sticky bar, ...).
 */
export function trackInstallButtonClicked(params: InstallFunnelEventParameters = {}) {
  trackEvent(EVENT_NAMES.INSTALL_BUTTON_CLICKED, {
    event_category: "install_funnel",
    event_label: params.source ?? "unknown",
    product_slug: params.product_slug ?? "unknown",
    source: params.source ?? "unknown",
    source_id: params.source_id ?? "unknown",
  });
}

/**
 * Track a click on the /install/ page's above-the-fold plan strip
 * (the 92%-never-see-pricing fix). The strip links to /pricing/<slug>/.
 */
export function trackInstallUpsellStripClicked(params: InstallFunnelEventParameters = {}) {
  trackEvent(EVENT_NAMES.INSTALL_UPSELL_STRIP_CLICKED, {
    event_category: "install_funnel",
    event_label: "install_strip",
    product_slug: params.product_slug ?? "unknown",
  });
}
